import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AdministrarContext } from "./context";
import SoftTypography from "components/SoftTypography";
import { Alert, Autocomplete, Grid, Icon, Input, TextField, Tooltip } from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import SoftInput from "components/SoftInput";
import { ConfirmarContext } from "context/ConfirmarContext";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import SoftBox from "components/SoftBox";
import { MessageManager } from "context";
import ModalHistorial from "./ModalHistorial";
import { getUproColor } from "utils/colors";
import { UserContext } from "context/user";
import PDFPliego from "assets/PDF/Bases_y_Condiciones_de_Compra.pdf";
import { PERMISOS_COMPRAS, puedeOperarAreaPaso } from "layouts/compras/permisos";
import { PASOS, evaluarRequisitosPaso, presupuestosRequeridos } from "./pasosCircuito";
import { mensajeError } from "layouts/compras/mensajeError";

const allowedFileTypes = ".doc,.docx,.pdf";

const GET_PROVEEDORES = gql`
  query getProveedores {
    proveedores(order: { field: "nombre", order: ASC }) {
      id
      tipoProveedor
      razonSocial
      nombre
      apellido
      tipoDocumento
      documento
      tipoResponsable
    }
  }
`;

const GET_NIVELES_AUTORIZACION = gql`
  query getNivelesAutorizacion {
    params(cod: "NIVELES_AUTORIZACION") {
      cod
      data
    }
  }
`;

// Áreas que operan los circuitos vigentes
const areaColors = {
  Solicitante: "uproPink",
  Compras: "uproYellow",
};


export default function Tab2() {
  // CONTEXTS
  const { handleSnackbar } = useContext(MessageManager);
  const { data, refetch, refetchParent, dataToSave, fullscreen } = useContext(AdministrarContext);
  const { confirmar } = useContext(ConfirmarContext);
  const { user, verificarAcceso } = useContext(UserContext);

  // STATES
  const [estados, setEstados] = useState([]);
  const [activeDoc, setActiveDoc] = useState(1);
  const [biggest, setBiggest] = useState(1);
  const [keyFile, setKeyFile] = useState(0);
  const [files, setFiles] = useState([]);
  const [modalHistorial, setModalHistorial] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [presupuestos, setPresupuestos] = useState([]);
  const [proveedoresSeleccionados, setProveedoresSeleccionados] = useState([]);
  const [collapsedPresupuestos, setCollapsedPresupuestos] = useState({});
  const [editingPliego, setEditingPliego] = useState(false);
  const [pliegoData, setPliegoData] = useState({ vencimientoDias: "", requiereDemanda: "", particularidades: "" });
  const [operando, setOperando] = useState(false);
  const listEstadosRef = useRef(null);

  // QUERIES
  const { data: proveedores } = useQuery(GET_PROVEEDORES);
  const { data: nivelesAutorizacion } = useQuery(GET_NIVELES_AUTORIZACION);
  const [getPedidoEstadosBase, { data: estadosData }] = useLazyQuery(gql`
    query GetPedidoEstadosBase($tipo: String!, $version: Int!) {
      pedidoCompraEstadosBase(filter: { tipo: $tipo, version: $version }) {
        id
        tipo
        orden
        nombre
        template
        obligatorio
        descripcion
        area
      }
    }
  `);

  const [mutateEstado] = useMutation(gql`
    mutation savePedidoCompraEstadoTab2($input: PedidoCompraEstadoInput!) {
      savePedidoCompraEstado(input: $input) {
        id
      }
    }
  `);

  const [deletePresupuesto] = useMutation(gql`
    mutation deletePedidoCompraEstado($id: ID!) {
      deletePedidoCompraEstado(id: $id)
    }
  `);

  const [mutateCancelarEstado] = useMutation(gql`
    mutation CANCELAR_ESTADO($idPedidoCompra: ID!, $motivo: String!) {
      cancelarPedidoCompraEstado(idPedidoCompra: $idPedidoCompra, motivo: $motivo) {
        id
      }
    }
  `);

  const [updatePliegoParticular] = useMutation(
    gql`
      mutation updatePedidoCompraPliego($input: PedidoCompraInput!) {
        savePedidoCompra(input: $input) {
          id
          pliegoVencimientoDias
          pliegoRequiereDemanda
          pliegoParticularidades
        }
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Información del pliego actualizada correctamente", "success");
        setEditingPliego(false);
        refetchParent();
        refetch();
      },
      onError: (error) => {
        handleSnackbar(mensajeError(error, "Error al actualizar la información del pliego"), "error");
      },
    }
  );

  const niveles = useMemo(() => {
    const raw = nivelesAutorizacion?.params?.find((p) => p.cod === "NIVELES_AUTORIZACION")?.data;
    if (!raw) return null;
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (error) {
      console.error("Error al leer NIVELES_AUTORIZACION:", error);
      return null;
    }
  }, [nivelesAutorizacion]);

  // EFFECTS
  const fetchPedidoEstadosBase = () => {
    getPedidoEstadosBase({ variables: { tipo: data.tipoCompra ?? "Base", version: data.version ?? 2 } });
  };

  useEffect(() => {
    if (data) {
      fetchPedidoEstadosBase();
    }
  }, [data]);

  useEffect(() => {
    if (!estadosData) return;
    const estadosBaseOrdenados = [...estadosData.pedidoCompraEstadosBase].sort((a, b) => a.orden - b.orden);
    if (!(data?.estados?.length > 0)) {
      setBiggest(0);
      setActiveDoc(0);
      setEstados(estadosBaseOrdenados);
      setPresupuestos([]);
      setProveedoresSeleccionados([]);
      return;
    }

    let mayor = 0;
    const baseConDocs = estadosBaseOrdenados.map((estadoBase) => {
      const estado = data.estados.find((e) => e.base.orden === estadoBase.orden);
      if (!estado) return estadoBase;
      if (estado.base.orden > mayor && estado.finalizado) mayor = estado.base.orden;
      return {
        ...estadoBase,
        fecha: dayjs(estado.updatedAt).format("DD/MM/YYYY"),
        file: estado.file,
        templateData: estado.templateData,
        finalizado: estado.finalizado,
        idEstado: estado.id,
        user: estado.user,
      };
    });

    if (data.tipoCompra && mayor == baseConDocs.length) {
      baseConDocs.push({
        id: -1,
        nombre: "Finalizado",
        descripcion: "El pedido ha sido finalizado",
        finalizado: false,
        obligatorio: true,
      });
    }
    setActiveDoc(mayor);
    setBiggest(mayor);
    setEstados(baseConDocs);

    // Presupuestos del paso CARGA DE PRESUPUESTO (un registro por proveedor)
    const presupuestosGuardados = data.estados
      .filter((e) => e.base.nombre === PASOS.CARGA_PRESUPUESTO)
      .map((estado) => ({
        id: estado.id,
        proveedor: estado.templateData?.proveedor || {},
        proveedorId: estado.templateData?.proveedor?.id,
        archivos: estado.file || [],
        fecha: estado.templateData?.fecha || estado.createdAt,
      }));
    setPresupuestos(presupuestosGuardados);
    setProveedoresSeleccionados(presupuestosGuardados.map((p) => p.proveedor).filter((p) => p && p.id));
  }, [estadosData, data]);

  useEffect(() => {
    if (listEstadosRef.current) {
      const activeEstado = listEstadosRef.current.querySelector(`#estado-${activeDoc}`);
      if (activeEstado) {
        const offsetTop = activeEstado.offsetTop - listEstadosRef.current.offsetTop;
        const centerOffset = listEstadosRef.current.clientHeight / 2 - activeEstado.clientHeight / 2;
        listEstadosRef.current.scrollTo({ top: offsetTop - centerOffset, behavior: "smooth" });
      }
    }
  }, [activeDoc]);

  useEffect(() => {
    if (editingPliego && data) {
      setPliegoData({
        vencimientoDias: data.pliegoVencimientoDias || "",
        // Sin valor por defecto: el usuario debe elegir SI o NO
        requiereDemanda: data.pliegoRequiereDemanda || "",
        particularidades: data.pliegoParticularidades || "",
      });
    }
  }, [editingPliego, data]);

  // DERIVADOS DEL PASO SELECCIONADO
  const estadoActual = estados[activeDoc];
  const esCargaPresupuesto = estadoActual?.nombre === PASOS.CARGA_PRESUPUESTO;
  const esCreador = data?.createdUser?.id == user?.id;

  const puedeOperar = puedeOperarAreaPaso(estadoActual?.area, {
    user,
    verificarAcceso,
    idCreador: data?.createdUser?.id,
  });

  // El paso seleccionado es el siguiente pendiente del circuito
  const esPasoPendiente = useMemo(() => {
    if (biggest > activeDoc) return false;
    const index = estados.findIndex((estado) => !estado.finalizado && estado.obligatorio);
    const indicePendiente = index === -1 ? estados.length : index;
    return indicePendiente === activeDoc || (!estadoActual?.obligatorio && indicePendiente === activeDoc + 1);
  }, [estados, activeDoc, biggest, estadoActual]);

  const puedeAutorizar =
    Boolean(estadoActual) && estadoActual.id != -1 && !estadoActual.finalizado && puedeOperar && esPasoPendiente;

  const puedeVolver =
    activeDoc > 0 && Boolean(estadoActual?.obligatorio) && !estadoActual?.finalizado && puedeOperar && esPasoPendiente;

  const requisitos = useMemo(
    () =>
      evaluarRequisitosPaso(estadoActual?.nombre, {
        pedido: data,
        detalles: data?.detalles || [],
        cantidadCotizaciones: data?.cotizaciones?.length ?? 0,
        cantidadPresupuestos: presupuestos.length,
        tieneOrdenProvision: (data?.detalles || []).some((d) => Boolean(d.provisionDetalle)),
        niveles,
      }),
    [estadoActual, data, presupuestos, niveles]
  );
  const requisitosCompletos = requisitos.every(([, cumple]) => cumple);

  // HANDLERS
  const toggleInput = () => {
    document.getElementById("input-file").click();
  };

  const handleFiles = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length > 0) {
      setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    }
  };

  // Sin índice limpia la selección; con índice (incluido el 0) quita solo ese archivo
  const handleRemoveFiles = (index) => {
    if (index === undefined) {
      setFiles([]);
    } else {
      setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    }
  };

  const recargar = () => {
    fetchPedidoEstadosBase();
    refetch();
    refetchParent();
  };

  const handleSavePliego = async () => {
    if (!["SI", "NO"].includes(pliegoData.requiereDemanda)) {
      handleSnackbar("Indique si el pedido requiere entregas a demanda", "error");
      return;
    }
    await updatePliegoParticular({
      variables: {
        input: {
          id: data.id,
          pliegoVencimientoDias: pliegoData.vencimientoDias ? parseInt(pliegoData.vencimientoDias) : null,
          pliegoRequiereDemanda: pliegoData.requiereDemanda,
          pliegoParticularidades: pliegoData.particularidades,
        },
      },
    }).catch(() => null);
  };

  const handleAgregarPresupuesto = () => {
    if (!selectedProveedor || files.length === 0) {
      handleSnackbar("Debe seleccionar un proveedor y cargar al menos un archivo", "error");
      return;
    }
    setPresupuestos((prev) => [
      ...prev,
      {
        id: Date.now(),
        proveedor: selectedProveedor,
        proveedorId: selectedProveedor.id,
        archivos: [...files],
        fecha: new Date().toISOString(),
      },
    ]);
    setProveedoresSeleccionados((prev) => [...prev, selectedProveedor]);
    setSelectedProveedor(null);
    setFiles([]);
    setKeyFile((prev) => prev + 1);
  };

  const handleEliminarPresupuesto = async (idPresupuesto) => {
    const presupuesto = presupuestos.find((p) => p.id === idPresupuesto);
    if (!presupuesto) return;
    try {
      // Un id de texto es un presupuesto guardado; uno numérico es temporal (solo en pantalla)
      if (typeof presupuesto.id === "string") {
        await deletePresupuesto({ variables: { id: presupuesto.id } });
        recargar();
      }
      setPresupuestos((prev) => prev.filter((p) => p.id !== idPresupuesto));
      setProveedoresSeleccionados((prev) => prev.filter((p) => p.id !== presupuesto.proveedorId));
      handleSnackbar("Presupuesto eliminado correctamente", "success");
    } catch (error) {
      handleSnackbar(mensajeError(error, "Error al eliminar el presupuesto"), "error");
    }
  };

  // Guarda como borrador los presupuestos que todavía están solo en pantalla
  const guardarPresupuestosTemporales = async () => {
    const guardados = [];
    for (const presupuesto of presupuestos) {
      if (typeof presupuesto.id !== "number") {
        guardados.push(presupuesto);
        continue;
      }
      const result = await mutateEstado({
        variables: {
          input: {
            idPedidoCompra: data.id,
            idBase: estadoActual.id,
            idProveedor: presupuesto.proveedor.id,
            file: presupuesto.archivos,
            templateData: { proveedor: presupuesto.proveedor, fecha: presupuesto.fecha },
            finalizado: false,
          },
        },
      });
      guardados.push({ ...presupuesto, id: result.data.savePedidoCompraEstado.id });
    }
    setPresupuestos(guardados);
    return guardados;
  };

  const handleSavePresupuestos = async () => {
    setOperando(true);
    try {
      await guardarPresupuestosTemporales();
      handleSnackbar("Presupuestos guardados correctamente", "success");
      recargar();
    } catch (error) {
      handleSnackbar(mensajeError(error, "Error al guardar los presupuestos"), "error");
    } finally {
      setOperando(false);
    }
  };

  const autorizar = async () => {
    setOperando(true);
    try {
      if (esCargaPresupuesto) {
        // Primero se guardan los presupuestos pendientes y después se autoriza cada uno:
        // el backend verifica la cantidad requerida al autorizar.
        const guardados = await guardarPresupuestosTemporales();
        for (const presupuesto of guardados) {
          await mutateEstado({ variables: { input: { id: presupuesto.id, finalizado: true } } });
        }
      } else if (estadoActual.idEstado) {
        await mutateEstado({ variables: { input: { id: estadoActual.idEstado, finalizado: true } } });
      } else {
        await mutateEstado({
          variables: { input: { idPedidoCompra: data.id, idBase: estadoActual.id, finalizado: true } },
        });
      }
      handleSnackbar("Paso autorizado", "success");
      handleRemoveFiles();
      recargar();
    } catch (error) {
      handleSnackbar(mensajeError(error, "Ha ocurrido un error, intente más tarde"), "error");
      recargar();
    } finally {
      setOperando(false);
    }
  };

  const handleAutorizar = () => {
    confirmar({
      title: "Autorizar paso",
      message: `¿Confirma la autorización de ${estadoActual?.nombre}?`,
      icon: "warning",
      func: autorizar,
    });
  };

  const handleVolver = () => {
    const pasoAnterior = estados[activeDoc - 1];
    const mensaje = [`El pedido volverá al paso ${pasoAnterior?.nombre ?? "anterior"}.`];
    if (estadoActual?.nombre === PASOS.CARGA_PRESUPUESTO) {
      mensaje.push("Se eliminarán los presupuestos cargados en este paso.");
    }
    if (estadoActual?.nombre === PASOS.ORDEN_PROVISION) {
      mensaje.push("Se anularán las órdenes de provisión emitidas.");
    }
    if (!esCreador) {
      mensaje.push(
        `Se notificará a ${data?.createdUser?.nombre ?? ""} ${data?.createdUser?.apellido ?? ""}, que creó el pedido.`.replace(/\s+,/, ",")
      );
    }
    mensaje.push("El motivo queda registrado en el historial.");

    confirmar({
      title: "Volver al paso anterior",
      message: mensaje.join(" "),
      icon: "warning",
      pedirMotivo: true,
      placeholder: "Ingrese el motivo",
      func: async (motivo) => {
        setOperando(true);
        try {
          await mutateCancelarEstado({ variables: { idPedidoCompra: data.id, motivo } });
          handleSnackbar("El pedido volvió al paso anterior", "success");
        } catch (error) {
          handleSnackbar(mensajeError(error, "Ha ocurrido un error, intente más tarde"), "error");
        } finally {
          setOperando(false);
          recargar();
        }
      },
    });
  };

  const toggleCollapsePresupuesto = (presupuestoId) => {
    setCollapsedPresupuestos((prev) => ({ ...prev, [presupuestoId]: !prev[presupuestoId] }));
  };

  const nombreArchivo = (archivo) =>
    typeof archivo === "string" ? archivo.split(/[/\\]/).pop() : archivo?.name || "";

  const recortar = (texto, max = 35) => (texto.length > max ? `${texto.substring(0, max)}...` : texto);

  const presupuestosMinimos = presupuestosRequeridos(data?.tipoCompra, data?.subtotalEstimado, niveles);

  let last = null;

  return (
    <Grid container spacing={2} position="relative">
      <SoftBox
        display={Object.keys(dataToSave).length > 0 ? "flex" : "none"}
        width="100%"
        height="100%"
        position="absolute"
        borderRadius="xl"
        sx={{ zIndex: 999, backdropFilter: "blur(3px)" }}
        justifyContent="center"
        alignItems="center"
      >
        <Alert severity="warning" sx={{ border: "1px solid" }}>
          Tiene cambios pendientes. Por favor, guarde los cambios antes de continuar.
        </Alert>
      </SoftBox>
      <Grid
        item
        xs={12}
        sm={6}
        xl={5}
        sx={{
          mt: 2,
          maxHeight: fullscreen ? "80vh" : "40vh",
          overflow: "auto",
          "& span": { background: "white" },
        }}
        ref={listEstadosRef}
      >
        <SoftBox display="flex" justifyContent="space-between">
          <SoftBox pl={4}></SoftBox>
          <SoftTypography variant="button">Estados</SoftTypography>
          <SoftBox pr={2}>
            <Tooltip title="Ver historial" placement="top">
              <Icon color="secondary" sx={{ cursor: "pointer" }} onClick={() => setModalHistorial(true)}>
                history
              </Icon>
            </Tooltip>
          </SoftBox>
        </SoftBox>
        {estados.map((estado, index) => {
          const current = estado.area;
          const text = current !== last ? current : null;
          last = current;
          return (
            <Grid
              id={`estado-${index}`}
              container
              position="relative"
              key={index}
              pt={text ? 2.2 : 0}
              sx={{ cursor: "pointer", userSelect: "none" }}
              onClick={() => {
                if (operando) return;
                setActiveDoc(index);
                handleRemoveFiles();
              }}
            >
              <SoftBox position="absolute" top={0} left="50%" lineHeight={0} sx={{ transform: "translateX(-50%)" }}>
                {text && (
                  <SoftBox
                    px={1.2}
                    py={0.5}
                    bgColor={getUproColor(areaColors[estado.area])}
                    fontSize="0.6rem"
                    color="white"
                    borderRadius="lg"
                    fontWeight="medium"
                    lineHeight={1}
                    sx={{ textTransform: "uppercase" }}
                  >
                    {text}
                  </SoftBox>
                )}
              </SoftBox>
              <Grid
                item
                xs={5.5}
                sm={5.5}
                sx={{ textAlign: "right", position: "relative" }}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
              >
                <SoftTypography component="label" variant="caption" fontWeight={activeDoc === index ? "bold" : "light"}>
                  {estado.nombre}
                  <br />
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight={activeDoc === index ? "bold" : "light"}
                    color="primary"
                  >
                    {estado.obligatorio ? "" : "(opcional)"}
                  </SoftTypography>
                </SoftTypography>
              </Grid>
              <Grid item xs={1} display="flex" justifyContent="center" alignItems="center" sx={{ position: "relative" }} py={2}>
                {index !== estados.length - 1 && (
                  <Grid
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "50%",
                      borderRight: "1px solid",
                      bottom: "0%",
                      right: "49%",
                      zIndex: 1,
                    }}
                  ></Grid>
                )}
                <Grid
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "50%",
                    borderRight: "1px solid",
                    top: "0%",
                    right: "49%",
                    zIndex: 2,
                  }}
                ></Grid>
                {activeDoc === index ? (
                  <Icon fontSize="small" sx={{ zIndex: 99, color: getUproColor(areaColors[estado.area]) }}>
                    circle
                  </Icon>
                ) : (
                  <Icon
                    fontSize="small"
                    sx={{
                      zIndex: 99,
                      color: getUproColor(index <= biggest ? areaColors[estado.area] : "secondary"),
                    }}
                  >
                    panorama_fish_eye_two_tone
                  </Icon>
                )}
              </Grid>
              <Grid
                item
                xs={5.5}
                sm={5.5}
                sx={{ textAlign: "left" }}
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
              >
                <SoftBox display="flex" alignItems="center" flexDirection="column">
                  {estado.finalizado && (
                    <>
                      <SoftBox display="flex" alignItems="center">
                        <Icon color={activeDoc === index ? "dark" : "secondary"} sx={{ fontSize: "1rem !important" }}>
                          person
                        </Icon>
                        <SoftTypography variant="caption" fontWeight={activeDoc === index ? "bold" : "light"}>
                          {estado.user?.nombre} {estado.user?.apellido}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center">
                        <Icon color={activeDoc === index ? "dark" : "secondary"} sx={{ fontSize: "1rem !important" }}>
                          done
                        </Icon>
                        <SoftTypography variant="caption" fontWeight={activeDoc === index ? "bold" : "light"}>
                          {estado.fecha || ""}
                        </SoftTypography>
                      </SoftBox>
                    </>
                  )}
                </SoftBox>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      {estados.length > 0 && (
        <Grid
          item
          xs={12}
          sm={6}
          xl={7}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          sx={{ mt: 1 }}
        >
          <SoftBox alignContent="flex-start">
            {estadoActual?.id != -1 && (
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="h6">Documentos</SoftTypography>
                  {estadoActual?.template && (
                    <Tooltip title="Plantilla de documento" placement="top">
                      <SoftButton
                        color="uproGreen"
                        iconOnly
                        circular
                        disabled={operando}
                        onClick={() => {
                          if (estadoActual.template.includes("templatesCompras")) {
                            window.open(resolveStoredFileUrl(estadoActual.template), "_blank");
                          } else if (estadoActual.template.includes("pliego-general")) {
                            window.open(PDFPliego, "_blank");
                          } else {
                            window.open(`/${estadoActual.template}/pdf/${data.id}`);
                          }
                        }}
                      >
                        <Icon>description</Icon>
                      </SoftButton>
                    </Tooltip>
                  )}
                </SoftBox>

                {/* Información del pliego particular (Proceso de compra) */}
                {data?.tipoCompra === "Concurso" && estadoActual?.nombre === PASOS.PLIEGO_PARTICULAR && (
                  <SoftBox mt={3} display="flex" justifyContent="center">
                    <SoftBox
                      p={2}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px",
                        backgroundColor: "#f8f9fa",
                        maxWidth: "800px",
                        width: "100%",
                      }}
                    >
                      <SoftBox display="flex" alignItems="center" gap={1} mb={2} justifyContent="space-between">
                        <SoftBox display="flex" alignItems="center" gap={1} justifyContent="center" flex={1}>
                          <Icon sx={{ color: "primary.main", fontSize: "1.5rem" }}>description</Icon>
                          <SoftTypography variant="h6" fontWeight="bold">
                            Información del Pliego Particular
                          </SoftTypography>
                        </SoftBox>
                        {/* Una vez iniciada la cotización el pliego queda fijo */}
                        {verificarAcceso(PERMISOS_COMPRAS.EDITAR_PLIEGO_PARTICULAR) && !data?.licitacionInicio && (
                          <Tooltip title={editingPliego ? "Guardar cambios" : "Editar pliego particular"} placement="top">
                            <SoftButton
                              color={editingPliego ? "success" : "info"}
                              iconOnly
                              circular
                              size="small"
                              onClick={editingPliego ? handleSavePliego : () => setEditingPliego(true)}
                              disabled={operando}
                            >
                              <Icon sx={{ fontSize: "1rem" }}>{editingPliego ? "save" : "edit"}</Icon>
                            </SoftButton>
                          </Tooltip>
                        )}
                      </SoftBox>

                      <Grid container spacing={2} direction="column">
                        <Grid item xs={12}>
                          <SoftBox p={1.5} sx={{ backgroundColor: "white", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ color: "info.main", fontSize: "1rem" }}>schedule</Icon>
                              <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Tiempo de vencimiento
                              </SoftTypography>
                            </SoftBox>
                            {editingPliego ? (
                              <SoftInput
                                type="number"
                                value={pliegoData.vencimientoDias || ""}
                                onChange={(e) => setPliegoData({ ...pliegoData, vencimientoDias: e.target.value })}
                                placeholder="Días de vencimiento"
                                size="small"
                              />
                            ) : (
                              <SoftTypography variant="body2" color="dark">
                                {data?.pliegoVencimientoDias ? `${data.pliegoVencimientoDias} días` : "No especificado"}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        <Grid item xs={12}>
                          <SoftBox p={1.5} sx={{ backgroundColor: "white", borderRadius: "8px", border: "1px solid #e9ecef" }}>
                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ color: "warning.main", fontSize: "1rem" }}>local_shipping</Icon>
                              <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Entregas a demanda
                              </SoftTypography>
                            </SoftBox>
                            {editingPliego ? (
                              <SoftBox display="flex" gap={1}>
                                <SoftButton
                                  size="small"
                                  color={pliegoData.requiereDemanda === "SI" ? "success" : "secondary"}
                                  onClick={() => setPliegoData({ ...pliegoData, requiereDemanda: "SI" })}
                                >
                                  SI
                                </SoftButton>
                                <SoftButton
                                  size="small"
                                  color={pliegoData.requiereDemanda === "NO" ? "error" : "secondary"}
                                  onClick={() => {
                                    confirmar({
                                      title: "Entrega inmediata",
                                      message:
                                        "Al seleccionar 'NO', se entenderá que el proveedor deberá entregar el pedido dentro de un plazo de 10 días a partir de la generación de la orden de provisión. ¿Desea continuar?",
                                      pedirMotivo: false,
                                      func: () => setPliegoData({ ...pliegoData, requiereDemanda: "NO" }),
                                    });
                                  }}
                                >
                                  NO
                                </SoftButton>
                              </SoftBox>
                            ) : (
                              <SoftBox display="flex" alignItems="center" gap={1}>
                                {data?.pliegoRequiereDemanda ? (
                                  <SoftBadge
                                    variant="contained"
                                    color={data.pliegoRequiereDemanda === "SI" ? "success" : "error"}
                                    size="sm"
                                    badgeContent={data.pliegoRequiereDemanda}
                                  />
                                ) : (
                                  <SoftTypography variant="body2" color="dark">
                                    No especificado
                                  </SoftTypography>
                                )}
                              </SoftBox>
                            )}
                          </SoftBox>
                        </Grid>

                        <Grid item xs={12}>
                          <SoftBox
                            p={1.5}
                            sx={{ backgroundColor: "white", borderRadius: "8px", border: "1px solid #e9ecef", minHeight: "80px" }}
                          >
                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                              <Icon sx={{ color: "secondary.main", fontSize: "1rem" }}>note_alt</Icon>
                              <SoftTypography variant="caption" fontWeight="bold" color="text">
                                Particularidades
                              </SoftTypography>
                            </SoftBox>
                            {editingPliego ? (
                              <SoftInput
                                multiline
                                rows={3}
                                value={pliegoData.particularidades || ""}
                                onChange={(e) => setPliegoData({ ...pliegoData, particularidades: e.target.value })}
                                placeholder="Ingrese las particularidades del pliego"
                                fullWidth
                              />
                            ) : (
                              <SoftTypography
                                variant="body2"
                                color="dark"
                                sx={{ maxHeight: "60px", overflowY: "auto", fontSize: "0.875rem", lineHeight: 1.4 }}
                              >
                                {data?.pliegoParticularidades || "No hay particularidades especificadas"}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>
                      </Grid>

                      {!data?.pliegoVencimientoDias && !data?.pliegoRequiereDemanda && !data?.pliegoParticularidades && (
                        <SoftBox
                          mt={2}
                          p={1.5}
                          sx={{
                            backgroundColor: "warning.light",
                            borderRadius: "8px",
                            borderLeft: "4px solid",
                            borderLeftColor: "warning.main",
                          }}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon sx={{ color: "warning.dark", fontSize: "1rem" }}>info</Icon>
                            <SoftTypography variant="caption" color="warning.dark">
                              No se ha completado la información del pliego particular. Complete los datos en la
                              pestaña &quot;Detalles&quot;.
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </SoftBox>
                )}

                {esCargaPresupuesto ? (
                  /* Carga de presupuestos: un registro por proveedor */
                  <SoftBox>
                    {puedeAutorizar && (
                      <SoftBox my={2} p={2} sx={{ border: "1px dashed #ccc", borderRadius: "8px" }}>
                        <SoftTypography variant="h6" mb={1}>
                          Agregar Presupuesto
                        </SoftTypography>
                        <SoftBox mb={2}>
                          <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                            Seleccionar Proveedor
                          </SoftTypography>
                          <Autocomplete
                            options={(proveedores?.proveedores || []).filter(
                              (proveedor) => !proveedoresSeleccionados.some((selected) => selected.id === proveedor.id)
                            )}
                            getOptionLabel={(proveedor) =>
                              [proveedor.razonSocial || proveedor.nombre, proveedor.apellido, ` - ${proveedor.documento}`]
                                .filter(Boolean)
                                .join(" ")
                            }
                            value={selectedProveedor}
                            onChange={(e, newValue) => setSelectedProveedor(newValue)}
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" placeholder="Buscar proveedor..." size="small" />
                            )}
                            noOptionsText="No hay proveedores disponibles"
                          />
                        </SoftBox>

                        {files.length > 0 && (
                          <SoftBox mb={2}>
                            <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                              Archivos seleccionados:
                            </SoftTypography>
                            {files.map((file, index) => (
                              <SoftBox
                                key={index}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                my={1}
                                p={1}
                                borderRadius="md"
                                sx={{ outline: "1px solid #e0e0e0" }}
                              >
                                <Tooltip title={file.name} placement="top">
                                  <SoftTypography variant="caption" sx={{ cursor: "help" }}>
                                    {recortar(file.name)}
                                  </SoftTypography>
                                </Tooltip>
                                <Tooltip title="Eliminar" placement="top">
                                  <Icon sx={{ cursor: "pointer" }} onClick={() => handleRemoveFiles(index)}>
                                    highlight_off
                                  </Icon>
                                </Tooltip>
                              </SoftBox>
                            ))}
                          </SoftBox>
                        )}

                        <SoftBox display="flex" gap={1} justifyContent="flex-end">
                          <SoftButton color="uproGreen" size="small" onClick={toggleInput} disabled={operando}>
                            <Icon>upload</Icon>&nbsp;Cargar Archivos
                          </SoftButton>
                          <SoftButton
                            color="primary"
                            size="small"
                            onClick={handleAgregarPresupuesto}
                            disabled={!selectedProveedor || files.length === 0 || operando}
                          >
                            <Icon>add</Icon>&nbsp;Agregar Presupuesto
                          </SoftButton>
                        </SoftBox>
                      </SoftBox>
                    )}

                    <SoftBox>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <SoftBox display="flex" alignItems="center" gap={2}>
                          <SoftTypography variant="h6">
                            Presupuestos Cargados ({presupuestos.length} de {presupuestosMinimos})
                          </SoftTypography>
                          {presupuestos.length > 1 && (
                            <SoftBox display="flex" gap={0.5}>
                              <Tooltip title="Expandir todos" placement="top">
                                <SoftButton
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  iconOnly
                                  onClick={() =>
                                    setCollapsedPresupuestos(Object.fromEntries(presupuestos.map((p) => [p.id, false])))
                                  }
                                >
                                  <Icon>unfold_more</Icon>
                                </SoftButton>
                              </Tooltip>
                              <Tooltip title="Colapsar todos" placement="top">
                                <SoftButton
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  iconOnly
                                  onClick={() =>
                                    setCollapsedPresupuestos(Object.fromEntries(presupuestos.map((p) => [p.id, true])))
                                  }
                                >
                                  <Icon>unfold_less</Icon>
                                </SoftButton>
                              </Tooltip>
                            </SoftBox>
                          )}
                        </SoftBox>
                        {presupuestos.some((p) => typeof p.id === "number") && (
                          <SoftBadge variant="contained" color="warning" size="sm" badgeContent="Hay cambios sin guardar" />
                        )}
                      </SoftBox>

                      {presupuestos.length > 0 ? (
                        presupuestos.map((presupuesto) => {
                          const isCollapsed = collapsedPresupuestos[presupuesto.id];
                          return (
                            <SoftBox
                              key={presupuesto.id}
                              mb={2}
                              sx={{ border: "1px solid #e0e0e0", borderRadius: "12px", overflow: "hidden" }}
                            >
                              <SoftBox
                                p={2}
                                sx={{
                                  backgroundColor: "#f8f9fa",
                                  borderBottom: isCollapsed ? "none" : "1px solid #e0e0e0",
                                  cursor: "pointer",
                                }}
                                onClick={() => toggleCollapsePresupuesto(presupuesto.id)}
                              >
                                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                                  <SoftBox display="flex" alignItems="center" gap={1} flex={1}>
                                    <Icon sx={{ color: "primary.main" }}>{isCollapsed ? "expand_more" : "expand_less"}</Icon>
                                    <SoftBox>
                                      <SoftBox display="flex" alignItems="center" gap={1}>
                                        <SoftTypography variant="body2" fontWeight="bold">
                                          {presupuesto.proveedor.razonSocial ||
                                            `${presupuesto.proveedor.nombre} ${presupuesto.proveedor.apellido}`}
                                        </SoftTypography>
                                        <SoftBadge
                                          variant="contained"
                                          color={typeof presupuesto.id === "string" ? "success" : "warning"}
                                          size="xs"
                                          badgeContent={typeof presupuesto.id === "string" ? "GUARDADO" : "TEMPORAL"}
                                        />
                                        <SoftBadge
                                          variant="contained"
                                          color="info"
                                          size="xs"
                                          badgeContent={`${presupuesto.archivos.length} archivo${presupuesto.archivos.length !== 1 ? "s" : ""}`}
                                        />
                                      </SoftBox>
                                      <SoftTypography variant="caption" color="secondary">
                                        CUIT/DNI: {presupuesto.proveedor.documento}
                                      </SoftTypography>
                                    </SoftBox>
                                  </SoftBox>
                                  {puedeAutorizar && (
                                    <Tooltip title="Eliminar presupuesto" placement="top">
                                      <Icon
                                        sx={{ cursor: "pointer", color: "error.main", "&:hover": { color: "error.dark" } }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEliminarPresupuesto(presupuesto.id);
                                        }}
                                      >
                                        delete
                                      </Icon>
                                    </Tooltip>
                                  )}
                                </SoftBox>
                              </SoftBox>

                              {!isCollapsed && (
                                <SoftBox p={2}>
                                  <SoftTypography variant="body2" fontWeight="bold" mb={1} color="text">
                                    Archivos del Presupuesto
                                  </SoftTypography>
                                  {presupuesto.archivos.map((archivo, archivoIndex) => (
                                    <SoftBox
                                      key={archivoIndex}
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      my={1}
                                      p={1.5}
                                      borderRadius="8px"
                                      sx={{ backgroundColor: "grey.50", border: "1px solid #e9ecef" }}
                                    >
                                      <SoftBox display="flex" alignItems="center" gap={1}>
                                        <Icon sx={{ color: "primary.main", fontSize: "1.2rem" }}>
                                          {nombreArchivo(archivo).toLowerCase().endsWith(".pdf") ? "picture_as_pdf" : "description"}
                                        </Icon>
                                        <Tooltip title={nombreArchivo(archivo)} placement="top">
                                          <SoftTypography variant="body2" sx={{ cursor: "help" }}>
                                            {recortar(nombreArchivo(archivo))}
                                          </SoftTypography>
                                        </Tooltip>
                                      </SoftBox>
                                      <Tooltip title="Ver archivo" placement="top">
                                        <Icon
                                          sx={{ cursor: "pointer", color: "primary.main" }}
                                          onClick={() =>
                                            window.open(
                                              typeof archivo === "string"
                                                ? resolveStoredFileUrl(archivo)
                                                : URL.createObjectURL(archivo),
                                              "_blank"
                                            )
                                          }
                                        >
                                          visibility
                                        </Icon>
                                      </Tooltip>
                                    </SoftBox>
                                  ))}
                                  {presupuesto.fecha && (
                                    <SoftTypography variant="caption" color="secondary">
                                      Fecha de carga: {dayjs(presupuesto.fecha).format("DD/MM/YYYY HH:mm")}
                                    </SoftTypography>
                                  )}
                                </SoftBox>
                              )}
                            </SoftBox>
                          );
                        })
                      ) : (
                        <SoftTypography variant="caption" color="secondary">
                          No hay presupuestos cargados
                        </SoftTypography>
                      )}
                    </SoftBox>

                    {puedeAutorizar && presupuestos.some((p) => typeof p.id === "number") && (
                      <SoftBox textAlign="center" mt={2}>
                        <SoftButton variant="outlined" color="success" circular onClick={handleSavePresupuestos} disabled={operando}>
                          Guardar presupuestos sin autorizar
                          <Icon sx={{ fontWeight: "bold" }}>check</Icon>
                        </SoftButton>
                      </SoftBox>
                    )}
                  </SoftBox>
                ) : (
                  /* Documentos guardados del paso (expedientes anteriores) */
                  <SoftBox>
                    {Array.isArray(estadoActual?.file) && estadoActual.file.length > 0 ? (
                      estadoActual.file.map((file, idx) => (
                        <SoftBox
                          key={idx}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          my={1}
                          p={1}
                          borderRadius="md"
                          sx={{ outline: "1px solid" }}
                        >
                          <SoftTypography
                            variant="caption"
                            pr={2}
                            sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
                          >
                            Archivo {idx + 1} - {nombreArchivo(file)}
                          </SoftTypography>
                          <Tooltip title="Ver archivo" placement="top">
                            <Icon
                              sx={{ cursor: "pointer", color: getUproColor("uproGreen") }}
                              onClick={() => window.open(resolveStoredFileUrl(file), "_blank")}
                            >
                              file_open
                            </Icon>
                          </Tooltip>
                        </SoftBox>
                      ))
                    ) : (
                      <SoftTypography variant="caption" color="secondary">
                        No hay archivos subidos
                      </SoftTypography>
                    )}
                  </SoftBox>
                )}
              </SoftBox>
            )}
            {requisitos.length > 0 && (
              <SoftBox>
                <SoftTypography variant="h6" mb={0.5}>
                  Requisitos
                </SoftTypography>
                <SoftBox sx={{ overflowY: "auto", maxHeight: "100px" }}>
                  {requisitos.map(([text, value], index) => (
                    <SoftBox key={index} display="flex" alignItems="center" gap={0.5}>
                      {value ? <Icon color="success">task_alt</Icon> : <Icon color="error">highlight_off</Icon>}
                      <SoftTypography variant="caption">{text}</SoftTypography>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
          <SoftBox display="flex" flexDirection="column" gap={1} p={1}>
            {puedeVolver && (
              <SoftButton color="error" fullWidth circular disabled={operando} onClick={handleVolver}>
                <Icon>undo</Icon>&nbsp; Volver a estado anterior&nbsp;
              </SoftButton>
            )}
            {estadoActual?.finalizado && estadoActual?.id != -1 && (
              <SoftBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon color="dark" sx={{ fontSize: "1rem !important" }}>
                    done
                  </Icon>
                  <SoftTypography variant="caption" fontWeight="bold">
                    AUTORIZADO
                  </SoftTypography>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon color="secondary" sx={{ fontSize: "1rem !important" }}>
                    person
                  </Icon>
                  <SoftTypography variant="caption">
                    {estadoActual.user?.nombre} {estadoActual.user?.apellido}
                  </SoftTypography>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" gap={0.5}>
                  <Icon color="secondary" sx={{ fontSize: "1rem !important" }}>
                    event
                  </Icon>
                  <SoftTypography variant="caption">{estadoActual.fecha || ""}</SoftTypography>
                </SoftBox>
              </SoftBox>
            )}
            {data.tipoCompra && estadoActual?.id == -1 && (
              <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                <Icon>done</Icon>
                <SoftTypography variant="caption" fontWeight="bold">
                  EXPEDIENTE FINALIZADO
                </SoftTypography>
              </SoftBox>
            )}
            {estadoActual?.area && !estadoActual?.finalizado && !puedeOperar && biggest == activeDoc && (
              <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.5} my={1}>
                <Icon>access_time</Icon>
                <SoftTypography variant="caption">Esperando aprobación de {estadoActual.area}</SoftTypography>
              </SoftBox>
            )}
            {puedeAutorizar && (
              <SoftButton
                variant="gradient"
                color="dark"
                fullWidth
                circular
                disabled={operando || !requisitosCompletos}
                onClick={handleAutorizar}
              >
                &nbsp; Autorizar &nbsp;
                <Icon sx={{ fontWeight: "bold" }}>send</Icon>
              </SoftButton>
            )}
          </SoftBox>
        </Grid>
      )}
      <SoftBox sx={{ display: "none" }}>
        <Input
          type="file"
          id="input-file"
          onChange={handleFiles}
          name="recibos"
          inputProps={{ accept: allowedFileTypes, multiple: true }}
          key={keyFile}
        />
      </SoftBox>
      <ModalHistorial open={modalHistorial} handleClose={() => setModalHistorial(false)} pedido={data} />
    </Grid>
  );
}
